<template>
  <!-- Offline State -->
  <div v-if="!isOnline" class="sm:py-12">
    <div class="sm:px-6 lg:px-8">
      <div class="flex flex-col justify-center items-center text-center sm:py-12">
        <div class="mb-6 text-6xl text-gray-400 dark:text-gray-600">
          <i class="pi pi-wifi"></i>
        </div>
        <h2 class="mb-2 text-2xl font-bold text-gray-700 dark:text-gray-300">You're offline</h2>
        <p class="mb-6 text-gray-600 dark:text-gray-400">
          Please check your internet connection to view business details and place orders.
        </p>
        <button
          @click="checkConnection"
          class="px-4 py-2 text-white bg-green-600 rounded hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2">
          Try Again
        </button>
      </div>
    </div>
  </div>

  <!-- Loading State -->
  <div v-else-if="isLoading" class="sm:py-12">
    <div class="sm:px-6 lg:px-8">
      <div class="flex justify-center items-center sm:py-12">
        <div
          class="w-12 h-12 rounded-full border-t-2 border-b-2 border-blue-500 animate-spin"></div>
      </div>
    </div>
  </div>

  <!-- Business Content -->
  <div class="sm:py-12" v-else-if="business">
    <div class="sm:px-6 lg:px-8">
      <!-- Business Details -->
      <div class="overflow-hidden mb-8 bg-white shadow-sm dark:bg-gray-800 sm:rounded-lg">
        <div class="p-6 text-gray-900 dark:text-gray-100">
            <div v-if="user && user.role === USER_ROLES.ADMIN && business.user?.is_deactivated"
              class="px-3 py-2 mb-4 text-sm text-amber-800 bg-amber-50 rounded-md border border-amber-200 dark:bg-amber-900/20 dark:text-amber-200 dark:border-amber-800">
              This business owner's account is deactivated. The store is hidden from public listings until the account is reactivated.
            </div>
          <div v-if="!user" class="inline-block p-2 mb-4 text-sm text-blue-600 bg-blue-100 rounded-md">You have to be
            logged in to place an order, you will be redirected to this business page after successfully logging in.
            <button
              @click="router.push({ name: ROUTES.LOGIN, query: { redirect: '/dashboard/businesses/'+ route.params.id } })"
              class="text-purple-600 underline hover:text-purple-900">Login Now</button>
          </div>
          <div class="grid grid-cols-1 gap-8 md:grid-cols-12">
            <div class="md:col-span-6">
              <h3 class="mb-4 text-lg font-semibold dark:text-white">Business Information</h3>
              <div v-if="business.logo_url" class="w-20 h-20">
                <img
                  :src="business.logo_url"
                  alt="Business Logo"
                  class="object-cover w-full h-full rounded-full" />
              </div>
              <div
                v-else
                class="flex justify-center items-center w-20 h-20 bg-gray-200 rounded-full dark:bg-gray-700">
                <span class="text-2xl font-semibold text-gray-600 dark:text-gray-300">
                  {{ getInitials(business.name) }}
                </span>
              </div>
              <div class="space-y-4">
                <div>
                  <p class="text-sm text-gray-500 dark:text-gray-400">Name</p>
                  <p class="mt-1">{{ business.name }}</p>
                </div>
                <div>
                  <p class="text-sm text-gray-500 dark:text-gray-400">Description</p>
                  <p class="mt-1">{{ business.description }}</p>
                </div>
                <div>
                  <p class="text-sm text-gray-500 dark:text-gray-400">Address</p>
                  <p class="mt-1">{{ business.address }}</p>
                </div>
                <!-- <div>
                  <p class="text-sm text-gray-500 dark:text-gray-400">Contact</p>
                  <p class="mt-1">{{ business.phone }}</p>
                  <p class="mt-1">{{ business.user.email }}</p>
                </div> -->
              </div>
            </div>
            <div class="md:col-span-4">
              <h3 class="mb-4 text-lg font-semibold">Rating & Reviews</h3>
              <div class="flex items-center mb-4">
                <div class="flex items-center">
                  <span class="mr-2 text-3xl font-bold">{{
                    (business.rating || 0).toFixed(1)
                    }}</span>
                  <div class="flex">
                    <template v-for="i in 5" :key="i">
                      <StarIcon
                        :class="[
                          'h-6 w-6',
                          i <= (business.rating || 0) ? 'text-yellow-400' : 'text-gray-300',
                        ]" />
                    </template>
                  </div>
                </div>
                <span class="ml-4 text-gray-500 dark:text-gray-400">
                  ({{ business.reviews_count || 0 }} reviews)
                </span>

              </div>
              <RouterLink :to="{
                name: user ? ROUTES.BUSINESS_REVIEW : ROUTES.PUBLIC_BUSINESS_REVIEW,
                params: { id: business.uuid },
              }" class="text-indigo-600 hover:text-indigo-900 dark:text-indigo-400 dark:hover:text-indigo-300">
                View all reviews
              </RouterLink>
            </div>
            <div v-if="user && user.role === USER_ROLES.ADMIN" class="relative">
              <Dropdown align="right" width="48">
                <template #trigger>
                  <ChevronDownIcon
                    class="w-5 h-5" />
                </template>

                <template #content>
                  <div class="block px-4 py-2 text-xs text-gray-400 sm:text-sm">Manage Business</div>
                  <DropdownLink as="button" @click="showBannerModal = true"> Generate Banner </DropdownLink>
                  <DropdownLink
                    v-if="business.user"
                    as="button"
                    @click="openOwnerDeactivationDialog">
                    {{ business.user.is_deactivated ? 'Reactivate business owner' : 'Deactivate business owner' }}
                  </DropdownLink>

                  <DropdownLink as="button" @click="showDeleteDialog = true"> Delete Business </DropdownLink>
                </template>
              </Dropdown>
            </div>

            <div class="md:col-span-2 md:justify-self-end" v-if="!business.is_active">
              <div
                class="p-2 w-auto text-xl font-semibold text-center text-red-800 rounded-md border border-red-800 md:text-2xl dark:text-red-400 dark:border-red-400">
                Closed
              </div>
            </div>
          </div>
        </div>
        <div class="col-span-3 p-6 mt-6" v-if="user && user.role === USER_ROLES.ADMIN">
          <div class="flex justify-end">
            <button
              class="px-6 py-2 text-white bg-green-800 rounded-xl cursor-pointer"
              @click="showQRCode = !showQRCode">
              {{ showQRCode ? 'Hide' : 'Show' }} QRCode
            </button>
          </div>
          <div v-if="showQRCode">
            <p class="text-sm text-gray-500 dark:text-gray-400">Business QR Code</p>
            <div class="mt-2">
              <QRCode
                :value="businessQRCodeUrl(business)"
                :size="150"
                :show-download="true"
                :show-print="true"
                :download-name="`business-${business?.uuid}`"
                :business-name="business?.name"
                alt="Business QR Code" />
              <p class="mt-2 text-xs text-gray-500 dark:text-gray-400">
                Scan this code to share this business page
              </p>
            </div>
          </div>
        </div>
      </div>

      <!-- Floating View Cart Button -->
      <div v-if="hasItemsInCart" class="fixed right-6 bottom-6 z-50">
        <button @click="scrollToCart"
          class="px-4 py-3 text-white bg-indigo-600 rounded-full shadow-lg hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2">
          View Cart ({{ cartItems.length }})
        </button>
      </div>

      <!-- Products Section -->
      <div class="overflow-hidden mb-8 bg-white shadow-sm dark:bg-gray-800 sm:rounded-lg">
        <div class="p-6 text-gray-900 dark:text-gray-100">
          <h3 class="mb-4 text-lg font-semibold dark:text-white">Products</h3>
          <FilterBar v-model="filters" :is-sub-categories="false" :categories="businessCategories" />
          <template v-if="business.products && business.products.length > 0">
            <div class="grid grid-cols-1 gap-6 md:grid-cols-2 xl:grid-cols-3">
              <div v-for="product in business.products" :key="product.id">
                <ProductCard :product="product" :business="business" :model-value="cart[product.id]?.quantity || 0"
                  @update:modelValue="(value) => updateQuantity(product, value)" />
              </div>
            </div>
            <div class="mt-6 text-center" v-if="!user"
              @click="router.push({ name: ROUTES.LOGIN, query: { redirect: '/dashboard/businesses/'+ route.params.id } })">
              <PrimaryButton variant="primary" size="large" class="mx-auto w-full sm:w-auto">
                Login to place an order
              </PrimaryButton>
            </div>
          </template>
          <div class="flex justify-center items-center" v-else>
            <div class="text-gray-900 dark:text-gray-100">No products for this business</div>
          </div>
        </div>
      </div>
      <!-- Order Summary -->
      <div v-if="hasItemsInCart" class="overflow-hidden bg-white shadow-sm dark:bg-gray-800 sm:rounded-lg" id="cart">
        <!-- <div>{{user}}</div> -->
        <div class="p-6 text-gray-900 dark:text-gray-100">
          <h3 class="mb-4 text-lg font-semibold dark:text-white">Order Summary</h3>
          <div class="space-y-4">
            <div v-for="item in cartItems" :key="item.id" class="flex justify-between items-center">
              <div>
                <p class="font-medium">{{ item.name }}</p>
                <p class="text-sm text-gray-500">Quantity: {{ item.quantity }}</p>
              </div>
              <div class="text-right">
                <p class="font-medium">
                  {{ nairaSymbol }}{{ (item.price * item.quantity).toFixed() }}
                </p>
                <PrimaryButton @click="removeFromCart(item)" variant="danger" size="small">
                  Remove
                </PrimaryButton>
              </div>
            </div>
            <div v-if="business.packs" id="packs">
              <h3 class="mb-4 text-lg font-semibold">Available Packs</h3>

              <div class="items-center space-y-2 sm:space-y-0 sm:justify-between sm:flex">
                <div>Packs: {{ nairaSymbol }}{{ business.packs }} each</div>
                <div class="flex items-center space-x-4">
                  <PrimaryButton @click="packs === 0 ? null : packs--">-</PrimaryButton>
                  <input type="number" v-model="packs" min="0" max="10" class="w-16 text-center" />
                  <PrimaryButton @click="packs === 10 ? null : packs++">+</PrimaryButton>
                </div>
              </div>
            </div>

            <div class="pt-4 border-t border-gray-200 dark:border-gray-700">
              <div class="flex justify-between items-center mb-2">
                <span class="text-gray-600 dark:text-gray-100">Subtotal</span>
                <span class="font-semibold text-gray-900 dark:text-gray-100">{{ nairaSymbol }}{{ subtotal.toFixed()
                  }}</span>
              </div>
              <div class="flex justify-between items-center mb-2">
                <span class="text-gray-600 dark:text-gray-100">Service Fee</span>
                <span class="font-semibold text-gray-900 dark:text-gray-100">{{ nairaSymbol }}{{ Math.min((subtotal *
                  serviceFeePercentage).toFixed(), maxCustomerServiceFee) }}</span>
              </div>
              <div class="flex justify-between items-center mb-2">
                <span class="text-gray-600 dark:text-gray-100">Delivery Fee</span>
                <span class="font-semibold text-gray-900 dark:text-gray-100">{{ nairaSymbol }}{{ deliveryFee }}</span>
              </div>
              <div class="flex justify-between items-center pt-2 border-t border-gray-200 dark:border-gray-700">
                <span class="font-semibold">Total</span>
                <span class="text-lg font-bold">{{ nairaSymbol }}{{ total }}</span>
              </div>
            </div>

            <!-- Delivery Address Form -->
            <div class="mt-8">
              <h4 class="mb-4 text-lg font-semibold">Delivery Information</h4>
              <div class="space-y-4">
                <div>
                  <label class="block text-sm font-medium text-gray-700 dark:text-gray-300">
                    Delivery Address
                  </label>
                  <textarea v-model="delivery_address" rows="3"
                    class="block p-3 mt-1 w-full rounded-md border-gray-300 shadow-sm dark:border-gray-700 focus:border-indigo-500 focus:ring-indigo-500 dark:bg-gray-700 dark:text-gray-300"
                    placeholder="Enter your delivery address"></textarea>
                  <p v-if="errors.delivery_address" class="mt-1 text-sm text-red-600">
                    {{ errors.delivery_address }}
                  </p>
                </div>
                <div>
                  <label class="block text-sm font-medium text-gray-700 dark:text-gray-300">
                    Phone
                  </label>
                  <input v-model="phone" type="tel"
                    class="block p-3 mt-1 w-full rounded-md border-gray-300 shadow-sm dark:border-gray-700 focus:border-indigo-500 focus:ring-indigo-500 dark:bg-gray-700 dark:text-gray-300"
                    placeholder="Enter your phone number" />
                  <p v-if="errors.phone" class="mt-1 text-sm text-red-600">
                    {{ errors.phone }}
                  </p>
                </div>
                <div>
                  <label class="block text-sm font-medium text-gray-700 dark:text-gray-300">
                    Additional Notes (Optional)
                  </label>
                  <textarea v-model="notes" rows="2"
                    class="block p-3 mt-1 w-full rounded-md border-gray-300 shadow-sm dark:border-gray-700 focus:border-indigo-500 focus:ring-indigo-500 dark:bg-gray-700 dark:text-gray-300"
                    placeholder="Any special instructions for delivery"></textarea>
                  <p v-if="errors.notes" class="mt-1 text-sm text-red-600">
                    {{ errors.notes }}
                  </p>
                </div>
              </div>
            </div>
            <div class="mt-6" v-if="user && user.role === USER_ROLES.CUSTOMER && business.is_active" @click="mixpanel.track('Place Order was clicked from Business Show Page', {business_name: business.name, business_uuid: business.uuid, orderItems: cartItems.map(item => item.name).join(', '), orderTotal: total})">
              <PrimaryButton @click="initializePayment" :disabled="processing" variant="primary" size="large" class="w-full">
                {{ processing ? 'Placing Order...' : 'Place Order' }}
              </PrimaryButton>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
  <div v-else>
    <div class="flex justify-center items-center h-screen">
      <div class="w-32 h-32 rounded-full border-b-2 border-gray-900 animate-spin"></div>
    </div>
  </div>
  <!-- Banner generator modal -->
  <BusinessBannerModal
    :show="showBannerModal"
    :business="business"
    @close="showBannerModal = false" />

  <ConfirmationModal :show="showOwnerDeactivationDialog" @close="showOwnerDeactivationDialog = false">
    <template #title>
      {{ business?.user?.is_deactivated ? 'Reactivate business owner' : 'Deactivate business owner' }}
    </template>
    <template #content>
      <p v-if="business?.user?.is_deactivated">
        The owner will be able to sign in again and the business can appear in public listings when it meets listing rules.
      </p>
      <p v-else>
        The owner will be signed out everywhere and cannot log in until reactivated. The business will be hidden from public listings.
      </p>
    </template>
    <template #footer>
      <SecondaryButton class="mr-2" @click="showOwnerDeactivationDialog = false">Cancel</SecondaryButton>
      <DangerButton :disabled="ownerDeactivationLoading" @click="confirmOwnerDeactivation">
        {{ ownerDeactivationLoading ? 'Working…' : 'Confirm' }}
      </DangerButton>
    </template>
  </ConfirmationModal>
  <ConfirmationModal :show="showDeleteDialog" @close="showDeleteDialog = false">
    <template #title>
      Delete Business
    </template>
    <template #content>
      <p>
        Are you sure you want to delete "{{ business?.name }}"? This action cannot be undone.
      </p>
    </template>
    <template #footer>
      <SecondaryButton class="mr-2" @click="showDeleteDialog = false">
        Cancel
      </SecondaryButton>
      <DangerButton @click="deleteBusiness">
        Delete
      </DangerButton>
    </template>
  </ConfirmationModal>
</template>

<script setup>
import PrimaryButton from '@/components/general/PrimaryButton.vue'
import { StarIcon } from '@heroicons/vue/24/solid'
import { ref, computed, onMounted, onUnmounted, watch, nextTick } from 'vue'
import ProductCard from '@/components/general/ProductCard.vue'
import { storeToRefs } from 'pinia'
import { useAuthStore } from '@/stores/authStore'
import { useFormValidation } from '@/composables/useFormValidation'
import { useField } from 'vee-validate'
import { orderSchema } from '@/formValidationschemas'
import { useBusinessStore } from '@/stores/businessStore'
import { useToast } from 'primevue/usetoast'
import { useRoute, useRouter } from 'vue-router'
import FilterBar from '@/components/general/FilterBar.vue'
import api from '@/services/axios'
import { getInitials } from '@/utils'
import { nairaSymbol, businessQRCodeUrl } from '@/constants'
import { USER_ROLES } from '@/types'
import { ROUTES } from '@/router/routeNames'
import Dropdown from '@/components/general/Dropdown.vue'
import DropdownLink from '@/components/general/DropdownLink.vue'
import ConfirmationModal from '@/components/general/ConfirmationModal.vue'
import SecondaryButton from '@/components/general/SecondaryButton.vue'
import DangerButton from '@/components/general/DangerButton.vue'
import BusinessBannerModal from '@/components/admin/BusinessBannerModal.vue'
import mixpanel from '@/services/mixpanel'
import PaystackPop from '@paystack/inline-js';
import QRCode from '@/components/general/QRCode.vue'
import { ChevronDownIcon } from '@heroicons/vue/24/outline'

// import { isPopupBlocked } from '@/utils/popupHandler'

const toast = useToast()
const showDeleteDialog = ref(false)
const showOwnerDeactivationDialog = ref(false)
const showBannerModal = ref(false)
const ownerDeactivationLoading = ref(false)

const showQRCode = ref(false);
const authStore = useAuthStore()
const { user } = storeToRefs(authStore)
const { handleSubmit, errors } = useFormValidation(orderSchema)
const businessStore = useBusinessStore()
const { business, businessCategories } = storeToRefs(businessStore)
const route = useRoute()
const isLoading = ref(false)
const isOnline = ref(navigator.onLine)
const router = useRouter()
const packs = ref(0)
const serviceFeePercentage = ref(0)
const deliveryFee = ref(0)
const maxCustomerServiceFee = ref(1500)
const paystackInstance = new PaystackPop();

const deleteBusiness = async () => {
  try {
    const response = await api.delete(`api/admin/businesses/${business.value.uuid}`)
    if (response.data) {
      toast.add({
        severity: 'success',
        summary: 'Success',
        detail: 'Business deleted successfully!',
        life: 3000,
      })
      showDeleteDialog.value = false
      router.push({ name: ROUTES.BUSINESSES })
    }
  } catch (error) {
    if(error.response.data.message) {
      toast.add({
        severity: 'error',
        summary: 'Error',
        detail: error.response.data.message,
        life: 3000,
      })
      return;
    }
    toast.add({
      severity: 'error',
      summary: 'Error',
      detail: 'Failed to delete business',
      life: 3000,
    })
  } finally {
    showDeleteDialog.value = false
  }
}

const openOwnerDeactivationDialog = () => {
  showOwnerDeactivationDialog.value = true
}

const confirmOwnerDeactivation = async () => {
  const ownerId = business.value?.user?.id
  if (!ownerId) {
    toast.add({ severity: 'error', summary: 'Error', detail: 'Owner information is not available.', life: 3000 })
    return
  }
  ownerDeactivationLoading.value = true
  const isDeactivated = !business.value.user.is_deactivated
  try {
    await api.patch(`/api/admin/users/${ownerId}/deactivation`, { is_deactivated: isDeactivated })
    await businessStore.fetchSingleBusiness(route.params.id, filters.value)
    toast.add({
      severity: 'success',
      summary: 'Success',
      detail: isDeactivated ? 'Business owner account has been deactivated.' : 'Business owner account has been reactivated.',
      life: 3000,
    })
    showOwnerDeactivationDialog.value = false
  } catch (error) {
    toast.add({
      severity: 'error',
      summary: 'Error',
      detail: error.response?.data?.message || 'Failed to update account',
      life: 4000,
    })
  } finally {
    ownerDeactivationLoading.value = false
  }
}
// Update online status when connectivity changes
const updateOnlineStatus = () => {
  isOnline.value = navigator.onLine
}

const filters = ref({ search: '', category: '', subcategory: '' })
const fetchBusinessAndCategories = async () => {
  if (isOnline.value) {
    isLoading.value = true
    try {
      const response = await api.get('api/constants')
      if (response.data) {
        serviceFeePercentage.value = response.data.customer_service_fee_percentage
        deliveryFee.value = response.data.customer_delivery_fee
        maxCustomerServiceFee.value = response.data.max_customer_service_fee
      } else {
        toast.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Cant order right now, please try again later',
          life: 3000,
        })
      }

      await businessStore.fetchSingleBusiness(route.params.id, filters.value)
      if(user.value) {
        await businessStore.fetchBusinessCategories(route.params.id)
      }
    } catch (err) {
      toast.add({
        severity: 'error',
        summary: 'Error',
        detail: 'Failed to fetch some business details',
        life: 3000,
      })
    } finally {
      isLoading.value = false
    }
  }
}
// Function to manually check connection and refresh data if online
const checkConnection = async () => {
  updateOnlineStatus()
  await fetchBusinessAndCategories()
}

onMounted(async () => {
  // Add event listeners for online/offline events
  window.addEventListener('online', updateOnlineStatus)
  window.addEventListener('offline', updateOnlineStatus)

  await fetchBusinessAndCategories();
  mixpanel.track('Business Page Loaded', {business_name: business.value.name, business_uuid: business.value.uuid})
})

onUnmounted(() => {
  // Clean up event listeners
  window.removeEventListener('online', updateOnlineStatus)
  window.removeEventListener('offline', updateOnlineStatus)
})
const { value: delivery_address } = useField('delivery_address')
const { value: phone } = useField('phone')
const { value: notes } = useField('notes')
const { value: items } = useField('items')
delivery_address.value = user.value?.address ?? ''
phone.value = user.value?.phone ?? ''

const cart = ref({})
const processing = ref(false)

const cartItems = computed(() => {
  return Object.values(cart.value).filter((item) => item.quantity > 0)
})

const hasItemsInCart = computed(() => {
  return cartItems.value.length > 0
})

// Define a function to scroll to cart that can be called directly
const scrollToCart = () => {
  // Force a DOM update before attempting to scroll
  nextTick(() => {
    const cartElement = document.getElementById('cart')
    if (cartElement) {
      // Use the simpler scrollIntoView method
      cartElement.scrollIntoView({
        behavior: 'smooth',
        block: 'start', // Align the top of the element with the top of the viewport
      })
    }
  })
}

const subtotal = computed(() => {
  let total = cartItems.value.reduce((sum, item) => sum + item.price * item.quantity, 0)
  if (business.value.packs) {
    total += packs.value * business.value.packs
  }
  return total
})

function applyFilters() {
  businessStore.fetchSingleBusiness(route.params.id, filters.value)
}

const total = computed(() => {
  const calculatedServiceFee = subtotal.value * (+serviceFeePercentage.value)
  const serviceFee = Math.min(calculatedServiceFee, maxCustomerServiceFee.value)
  return (subtotal.value + serviceFee + (+deliveryFee.value)).toFixed()
})

watch(() => filters.value, applyFilters, { deep: true })

// const increaseQuantity = (product) => {
//   if (!cart.value[product.id]) {
//     cart.value[product.id] = { ...product, quantity: 0 }
//   }
//   cart.value[product.id].quantity++

//   // Scroll to cart when first item is added
//   // if (Object.keys(cart.value).length === 1 && cart.value[product.id].quantity === 1) {
//   //   scrollToCart()
//   // }
// }

// const decreaseQuantity = (product) => {
//   if (cart.value[product.id]) {
//     if (cart.value[product.id].quantity > 0) {
//       cart.value[product.id].quantity--
//       if (cart.value[product.id].quantity === 0) {
//         delete cart.value[product.id]
//       }
//     }
//   }
// }

const removeFromCart = (item) => {
  delete cart.value[item.id]
}

const updateQuantity = (product, value) => {
  // const isFirstItem = Object.keys(cart.value).length === 0

  if (!cart.value[product.id]) {
    cart.value[product.id] = { ...product, quantity: 0 }
  }
  cart.value[product.id].quantity = value
  if (value === 0) {
    delete cart.value[product.id]
  }

  // Scroll to cart when first item is added
  // if (isFirstItem && value > 0) {
  //   scrollToCart()
  // }
}

const initializePayment = handleSubmit(async () => {

  if (user.value && route.name === ROUTES.PUBLIC_BUSINESS) {
    router.push({ name: ROUTES.BUSINESS_SHOW, params: { id: route.params.id } })
    return
  }
  if (!user.value) {
    toast.add({
      severity: 'error',
      summary: 'Error',
      detail: 'Please login to place an order.',
      life: 3000,
    })
    router.push({ name: 'login', query: { redirect: route.fullPath } })
    return
  }
  if (Object.keys(cartItems.value).length === 0) {
    toast.add({
      severity: 'error',
      summary: 'Error',
      detail: 'Your cart is empty. Please add items before placing an order.',
      life: 3000,
    })
    return
  }

  if (!delivery_address.value) {
    toast.add({
      severity: 'error',
      summary: 'Error',
      detail: 'Please provide a delivery address.',
      life: 3000,
    })
    return
  }

  if (!phone.value) {
    toast.add({
      severity: 'error',
      summary: 'Error',
      detail: 'Please provide a contact phone number.',
      life: 3000,
    })
    return
  }

  if (business.value.packs && business.value.packs > 0 && !packs.value) {
    nextTick(() => {
      const packsElement = document.getElementById('packs')
      if (packsElement) {
        // Use the simpler scrollIntoView method
        packsElement.scrollIntoView({
          behavior: 'smooth',
          block: 'start', // Align the top of the element with the top of the viewport
        })
      }
    })
    toast.add({
      severity: 'error',
      summary: 'Error',
      detail: 'Please select at least one pack.',
      life: 3000,
    })
    return
  }

  paystackInstance.newTransaction({
    key: import.meta.env.VITE_PAYSTACK_PUBLIC_KEY,
    email: user.value.email,
    amount: total.value * 100,
    channels: ['card', 'bank', 'ussd', 'qr', 'eft', 'mobile_money', 'bank_transfer', 'apple_pay'],
    onSuccess: async (transaction) => {
      await submitOrder(transaction.reference)
    },
    onError: (error) => {
      console.error('Payment error:', error)
      toast.add({ summary: 'Failed', detail: error.message, severity: 'error', life: 5000 })
    }
  });
})

const submitOrder = async (reference) => {

  if (!reference) {
    toast.add({
      severity: 'error',
      summary: 'Error',
      detail: 'Please make sure your browser allows popups to make payment and continue.',
      life: 3000,
    })
    return
  }

  const calculatedServiceFee = subtotal.value * (+serviceFeePercentage.value)
  const serviceFee = Math.min(calculatedServiceFee, maxCustomerServiceFee.value)

  const orderData = {
    items: cartItems.value.map((item) => ({
      product_id: item.id,
      quantity: item.quantity,
      price: item.price,
      name: item.name,
      description: item.description,
    })),
    delivery_address: delivery_address.value,
    phone: phone.value,
    notes: notes.value,
    subtotal: subtotal.value,
    service_fee: serviceFee,
    packs: packs.value,
    service_fee_percentage: (+serviceFeePercentage.value) * 100,
    delivery_fee: deliveryFee.value,
    total_amount: total.value,
  }

  try {
    processing.value = true
    const response = await api.post('api/customer/orders', {
      business: business.value.id,
      ...orderData,
      reference: reference,
    })

    // Clear the cart and form
    cart.value = {}
    items.value = []
    notes.value = ''
    packs.value = 0

    // Show success message
    toast.add({
      severity: 'success',
      summary: 'Success',
      detail: 'Order placed successfully',
      life: 3000,
    })
    if (response.data) {
      router.push({ name: ROUTES.DASHBOARD_ORDER, params: {id: response.data.order.uuid}})
    } else {
      toast.add({
        summary: 'Failed',
        detail: response.error,
        severity: 'error',
        life: 5000
      })
    }
  } catch (error) {
    console.error('Order submission failed:', error)

    if (error.response?.data?.errors) {
      const errors = error.response.data.errors
      if (errors.items) {
        toast.add({
          severity: 'error',
          summary: 'Error',
          detail: 'There was an issue with your order items. Please try again.',
          life: 3000,
        })
      } else if (errors.delivery_address) {
        toast.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Please provide a valid delivery address.',
          life: 3000,
        })
      } else if (errors.phone) {
        toast.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Please provide a valid phone number.',
          life: 3000,
        })
      } else {
        toast.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Failed to place order. Please try again later.',
          life: 3000,
        })
      }
    } else {
      toast.add({
        severity: 'error',
        summary: 'Error',
        detail: 'An unexpected error occurred. Please try again later.',
        life: 3000,
      })
    }
  } finally {
    processing.value = false
  }
}

// import { openExternalLink } from '@/utils/externalLinks'

// const handlePayment = async (paymentUrl) => {
//   try {
//     if (paymentUrl) {
//       // Use the external links utility which handles PWA vs browser mode
//       await openExternalLink(paymentUrl, true, {
//         returnUrl: window.location.href,
//         businessId: business.value?.id,
//       })
//     }
//   } catch (error) {
//     console.error('Payment error:', error)
//     toast.add({
//       severity: 'error',
//       summary: 'Payment Error',
//       detail: 'There was a problem processing your payment. Please try again.',
//       life: 5000,
//     })
//     // Fallback in case of any errors
//     window.location.href = paymentUrl
//   }
// }
</script>
